/**
 * Helpers de robustez para el BulkUploader.
 *
 * - createTokenBucket: rate limiter cooperativo (N ops/seg)
 * - createCircuitBreaker: corta requests tras N errores seguidos en ventana
 * - retryWithBackoff: reintenta una operación async con backoff exponencial
 * - createAdaptiveConcurrency: ajusta workers según degradación percibida
 * - isPermanentError: clasifica errores irrecuperables
 * - logAnalyticsEvent: telemetría (Firebase Analytics) — best-effort
 * - assertCanUploadTo: validación previa de permisos sobre la carpeta destino
 * - yieldToEventLoop: cede ejecución al browser para no bloquear la UI
 */

// Códigos / patterns que se consideran permanentes y NO se reintentan.
// El catch externo los marca directamente como fallidos.
const PERMANENT_ERROR_CODES = new Set([
  'storage/unauthorized',
  'storage/unauthenticated',
  'storage/quota-exceeded',
  'storage/object-not-found',
  'storage/invalid-argument',
  'permission-denied',
  // Códigos Firestore "pelados" (batch.commit): deterministas, no reintentar.
  'invalid-argument',
  'unauthenticated',
  'not-found',
  'already-exists',
]);

const PERMANENT_ERROR_NAMES = new Set([
  'NotReadableError', // archivo bloqueado: reintentar es inútil sin acción del usuario
  'NotFoundError',    // archivo eliminado/movido
]);

// Patrones de mensaje que indican un límite de tamaño de documento Firestore
// (1 MiB) — es determinista, reintentar no ayuda.
const PERMANENT_SIZE_PATTERNS = ['exceeds the maximum', 'longer than', 'too large', 'maximum size'];

export const isPermanentError = (err) => {
  if (!err) return false;
  if (PERMANENT_ERROR_NAMES.has(err.name)) return true;
  if (PERMANENT_ERROR_CODES.has(err.code)) return true;
  const msg = (err.message || '').toLowerCase();
  if (msg.includes('could not be read')) return true;
  if (msg.includes('unauthorized')) return true;
  if (msg.includes('permission denied')) return true;
  if (PERMANENT_SIZE_PATTERNS.some(p => msg.includes(p))) return true;
  return false;
};

// ─── retry con backoff exponencial ──────────────────────────────────
// `attempts` = total de intentos (incluyendo el primero).
// El delay entre intentos crece exponencialmente con cap en `maxDelay`,
// y suma aproximadamente `targetTotalMs` en total para dar tiempo a que
// la red se recupere.
//
// El sleep entre intentos es INTERRUMPIBLE:
//  - Si isAbort() pasa a true → sale inmediatamente
//  - Si onRetryWake() retorna true → corta el sleep para reintentar antes
//    (típicamente: red volvió de offline a online)
//
// Configuración por defecto (5 min total):
//   attempts=10, baseDelay=2000, factor=2, maxDelay=60000
//   delays: 2s, 4s, 8s, 16s, 32s, 60s, 60s, 60s, 60s ≈ 302s total
export const retryWithBackoff = async (fn, {
  attempts = 10,
  baseDelay = 2000,
  factor = 2,
  maxDelay = 60000,
  jitter = 0.2,
  isAbort = () => false,
  isOffline = () => false,
  // Clasificador de errores irrecuperables. Por defecto isPermanentError;
  // un llamador puede sustituirlo cuando en SU contexto hay errores que no
  // vale la pena reintentar aunque sean transitorios en general — el caso
  // real es la contabilizacion masiva, cuyo thunk ya reintenta el put de
  // Storage por dentro: reintentar otra vez desde afuera multiplica el peor
  // caso por fila y congela la cola secuencial.
  isPermanent = isPermanentError,
  onRetryWake = null,       // si retorna true durante el sleep, lo aborta
  onAttempt = null,         // callback(attemptNumber)
  onWaiting = null,         // callback(delayMs, attemptNumber)
} = {}) => {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    if (isAbort()) throw new Error('__ABORTED__');
    if (isOffline()) throw new Error('__OFFLINE__');
    try {
      if (onAttempt) onAttempt(i + 1);
      return await fn(i + 1);
    } catch (err) {
      lastErr = err;
      if (isPermanent(err)) throw err;           // no reintentar permanentes
      if (i === attempts - 1) break;              // último intento → propagar
      if (isAbort()) throw new Error('__ABORTED__');

      // Calcula delay: baseDelay * factor^i, con cap en maxDelay
      const raw = Math.min(baseDelay * Math.pow(factor, i), maxDelay);
      const jitterAmount = raw * jitter * (Math.random() * 2 - 1);
      const delay = Math.max(0, raw + jitterAmount);

      if (onWaiting) onWaiting(delay, i + 2);

      // Sleep interrumpible: chequea cada 500ms si hay que abortar o
      // si onRetryWake quiere acelerar el reintento.
      const start = Date.now();
      await new Promise((resolve) => {
        const tick = setInterval(() => {
          if (isAbort()) { clearInterval(tick); resolve(); return; }
          if (onRetryWake && onRetryWake()) { clearInterval(tick); resolve(); return; }
          if (Date.now() - start >= delay) { clearInterval(tick); resolve(); return; }
        }, 500);
      });

      if (isAbort()) throw new Error('__ABORTED__');
    }
  }
  throw lastErr;
};

// ─── Token bucket: limita N operaciones por segundo ─────────────────
// Cada worker llama `await bucket.take()` antes de hacer la operación.
// El bucket se rellena `rate` tokens/seg hasta `capacity`.
export const createTokenBucket = ({ rate = 5, capacity = 10 } = {}) => {
  let tokens = capacity;
  let lastRefill = Date.now();

  const refill = () => {
    const now = Date.now();
    const elapsed = (now - lastRefill) / 1000;
    tokens = Math.min(capacity, tokens + elapsed * rate);
    lastRefill = now;
  };

  const take = async () => {
    refill();
    if (tokens >= 1) {
      tokens -= 1;
      return;
    }
    // Espera hasta que haya un token disponible
    const wait = ((1 - tokens) / rate) * 1000;
    await new Promise(r => setTimeout(r, wait));
    refill();
    tokens = Math.max(0, tokens - 1);
  };

  return {
    take,
    setRate: (r) => { rate = r; },
    setCapacity: (c) => { capacity = c; tokens = Math.min(tokens, c); }
  };
};

// ─── Circuit breaker ────────────────────────────────────────────────
// Tres estados: closed (normal), open (cortado), half-open (probando).
// Se "abre" tras N fallos en una ventana, queda abierto cooldownMs antes
// de probar un request.
export const createCircuitBreaker = ({
  failureThreshold = 5,
  windowMs = 30 * 1000,
  cooldownMs = 30 * 1000,
  onStateChange = () => {}
} = {}) => {
  let failures = []; // timestamps de fallos recientes
  let state = 'closed';
  let openedAt = 0;

  const transition = (next) => {
    if (state !== next) {
      state = next;
      onStateChange(next);
    }
  };

  return {
    canPass: () => {
      const now = Date.now();
      if (state === 'open') {
        if (now - openedAt > cooldownMs) {
          transition('half-open');
          return true;
        }
        return false;
      }
      return true;
    },

    recordSuccess: () => {
      failures = [];
      transition('closed');
    },

    recordFailure: () => {
      const now = Date.now();
      failures.push(now);
      // limpia fallos fuera de la ventana
      failures = failures.filter(t => (now - t) < windowMs);
      if (failures.length >= failureThreshold && state !== 'open') {
        openedAt = now;
        transition('open');
      } else if (state === 'half-open') {
        // fallo en half-open → vuelve a open
        openedAt = now;
        transition('open');
      }
    },

    getState: () => state,
    cooldownRemainingMs: () => state === 'open' ? Math.max(0, cooldownMs - (Date.now() - openedAt)) : 0,
    reset: () => { failures = []; transition('closed'); }
  };
};

// ─── Concurrencia adaptativa ────────────────────────────────────────
// Mantiene una ventana móvil de duraciones de upload. Si la mediana
// reciente supera 2x la mediana baseline, baja workers; si vuelve a
// normalizarse, los sube.
export const createAdaptiveConcurrency = ({
  initial = 3,
  min = 1,
  max = 6,
  windowSize = 10,
  degradationFactor = 2.0
} = {}) => {
  let current = initial;
  let durations = [];
  let baseline = null;

  const median = (arr) => {
    if (!arr.length) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  };

  return {
    recordDuration: (ms) => {
      durations.push(ms);
      if (durations.length > windowSize) durations.shift();

      // Establece baseline tras los primeros windowSize uploads
      if (durations.length >= windowSize && baseline === null) {
        baseline = median(durations);
        return;
      }

      if (baseline !== null && durations.length >= 5) {
        const recent = median(durations.slice(-5));
        if (recent > baseline * degradationFactor && current > min) {
          current = Math.max(min, current - 1);
        } else if (recent < baseline * 1.2 && current < max) {
          current = Math.min(max, current + 1);
        }
      }
    },
    getCurrent: () => current,
    reset: () => { durations = []; baseline = null; current = initial; }
  };
};

// ─── Telemetría a Firebase Analytics (best-effort) ─────────────────
// El proyecto no inicializa Analytics explícitamente; intentamos via
// window.firebase si existe. Si no, log a consola y seguimos.
export const logAnalyticsEvent = (eventName, params = {}) => {
  try {
    // Firebase 8 compat / 9 modular: ambos lo exponen vía window si está
    const fb = (typeof window !== 'undefined') ? window.firebase : null;
    if (fb?.analytics) {
      fb.analytics().logEvent(eventName, params);
      return;
    }
  } catch { /* silent */ }
  // Fallback: log a consola con prefijo identificable
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.info(`[telemetry] ${eventName}`, params);
  }
};

// ─── Timeout por intento proporcional al tamaño ────────────────────
// Timeout (ms) para UN intento de subida, proporcional al tamaño del archivo,
// con piso y tope. Un archivo grande "aguanta" más antes de considerarse estancado.
export const attemptTimeoutForSize = (sizeBytes, {
  minMs = 180000,
  maxMs = 600000,
  throughputBps = 262144, // 256 KB/s: piso de conexión lenta
} = {}) => {
  const size = Number(sizeBytes) || 0;
  const proportional = (size / throughputBps) * 1000;
  return Math.max(minMs, Math.min(maxMs, proportional));
};

// Reordena en dos fases: primero los < threshold (orden estable), luego los
// >= threshold (orden estable). No muta el input.
export const orderBySizePhase = (items, thresholdBytes) => {
  const small = [];
  const large = [];
  for (const it of items || []) {
    if ((Number(it?.size) || 0) < thresholdBytes) small.push(it);
    else large.push(it);
  }
  return small.concat(large);
};

// ─── Validación de permisos previa al upload ────────────────────────
// Lee el doc destino y comprueba que es accesible. NO valida la matriz
// completa de permisos (eso lo hacen Firestore Rules en cada write);
// solo confirma que la carpeta existe y es legible.
export const assertCanUploadTo = async (targetFolderRef) => {
  if (!targetFolderRef) throw new Error('No hay carpeta destino.');
  try {
    if (typeof targetFolderRef.doc !== 'function') {
      // DocumentReference (raíz del cliente folders/{cid}): solo validar que
      // sea LEGIBLE. Que no exista NO es error — el doc raíz se crea de forma
      // perezosa (clientes nuevos/vacíos no lo tienen) y la fase de árbol lo
      // crea con el primer arrayUnion de folders[].
      await targetFolderRef.get();
    } else {
      // CollectionReference: validamos su doc padre (donde vive folders[]).
      const parent = targetFolderRef.parent || null;
      if (parent && parent.get) {
        const snap = await parent.get();
        if (!snap.exists) throw new Error('La carpeta destino ya no existe.');
      }
    }
  } catch (e) {
    // Aclarar permission-denied vs otros
    if (e?.code === 'permission-denied') {
      throw new Error('No tienes permisos para subir a esta carpeta.');
    }
    throw e;
  }
};

// ─── Yield al event loop (alternativa práctica a Web Workers) ───────
// Firebase SDK no funciona dentro de un Web Worker, así que en lugar de
// movernos a un worker real, cedemos control al browser cada N items
// para que la UI siga responsive.
export const yieldToEventLoop = () => new Promise(resolve => {
  // setTimeout(0) tiene min ~4ms; MessageChannel es más rápido (~0.1ms)
  if (typeof MessageChannel !== 'undefined') {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => resolve();
    channel.port2.postMessage(null);
  } else {
    setTimeout(resolve, 0);
  }
});

// ─── Límites configurables del batch ────────────────────────────────
export const DEFAULT_LIMITS = {
  maxFilesPerBatch: 10000,
  maxSizePerFile: 100 * 1024 * 1024,      // 100 MB
  maxTotalSize: 20 * 1024 * 1024 * 1024,  // 20 GB
};

export const validateBatchLimits = (files, limits = DEFAULT_LIMITS) => {
  if (files.length > limits.maxFilesPerBatch) {
    return {
      ok: false,
      reason: `Máximo ${limits.maxFilesPerBatch.toLocaleString('es-CO')} archivos por lote. Recibido: ${files.length.toLocaleString('es-CO')}.`
    };
  }
  for (const f of files) {
    if (f.size > limits.maxSizePerFile) {
      const mb = (limits.maxSizePerFile / 1024 / 1024).toFixed(0);
      return {
        ok: false,
        reason: `"${f.name}" excede el límite de ${mb} MB por archivo.`
      };
    }
  }
  const total = files.reduce((s, f) => s + f.size, 0);
  if (total > limits.maxTotalSize) {
    const gb = (limits.maxTotalSize / 1024 / 1024 / 1024).toFixed(0);
    return {
      ok: false,
      reason: `El tamaño total supera el límite de ${gb} GB.`
    };
  }
  return { ok: true };
};
